/**
 * SEOFields — SEO & Meta tags section body. Extracted from CMSEditor.jsx.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ExternalLink } from "lucide-react";
import MediaUploader from "@/components/MediaUploader";
import { Grid2, Hint, Code, Field, FieldFull } from "./primitives";

export function SEOFields({ draft, set }) {
  return (
    <>
      <Hint>Controls what search engines &amp; social platforms see. Feeds <Code>/robots.txt</Code>, <Code>/sitemap.xml</Code>, and JSON-LD.</Hint>
      <Grid2>
        <FieldFull label="Page title (browser tab / Google result)">
          <Input value={draft.seo?.title || ""} onChange={(e) => set("seo.title", e.target.value)} maxLength={70} data-testid="cms-seo-title" />
          <div className="mt-1 text-[10px] text-muted-foreground">{(draft.seo?.title || "").length}/70 characters (Google truncates ~60)</div>
        </FieldFull>
        <FieldFull label="Meta description (Google snippet)">
          <Textarea rows={3} value={draft.seo?.description || ""} onChange={(e) => set("seo.description", e.target.value)} maxLength={200} data-testid="cms-seo-description" />
          <div className="mt-1 text-[10px] text-muted-foreground">{(draft.seo?.description || "").length}/200 characters (Google shows ~155)</div>
        </FieldFull>
        <FieldFull label="Keywords (comma-separated)">
          <Textarea rows={2} value={draft.seo?.keywords || ""} onChange={(e) => set("seo.keywords", e.target.value)} data-testid="cms-seo-keywords" />
        </FieldFull>
        <Field label="Canonical URL"><Input value={draft.seo?.canonical_url || ""} onChange={(e) => set("seo.canonical_url", e.target.value)} placeholder="https://fuelexpress.in/" data-testid="cms-seo-canonical" /></Field>
        <Field label="Author / Publisher"><Input value={draft.seo?.author || ""} onChange={(e) => set("seo.author", e.target.value)} /></Field>
        <Field label="Open Graph image URL (1200×630)"><Input value={draft.seo?.og_image_url || ""} onChange={(e) => set("seo.og_image_url", e.target.value)} placeholder="https://…/og-cover.jpg" /></Field>
        <Field label="OG Type">
          <select value={draft.seo?.og_type || "website"} onChange={(e) => set("seo.og_type", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            <option value="website">website</option>
            <option value="article">article</option>
            <option value="business.business">business.business</option>
            <option value="product">product</option>
          </select>
        </Field>
        <Field label="Twitter handle (e.g. @fuelexpress)"><Input value={draft.seo?.twitter_handle || ""} onChange={(e) => set("seo.twitter_handle", e.target.value)} /></Field>
        <Field label="Twitter card style">
          <select value={draft.seo?.twitter_card || "summary_large_image"} onChange={(e) => set("seo.twitter_card", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            <option value="summary_large_image">summary_large_image</option>
            <option value="summary">summary</option>
            <option value="app">app</option>
            <option value="player">player</option>
          </select>
        </Field>
        <Field label="Robots directive">
          <select value={draft.seo?.robots || "index, follow"} onChange={(e) => set("seo.robots", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="cms-seo-robots">
            <option value="index, follow">index, follow (visible on Google)</option>
            <option value="noindex, follow">noindex, follow</option>
            <option value="index, nofollow">index, nofollow</option>
            <option value="noindex, nofollow">noindex, nofollow (fully private)</option>
          </select>
        </Field>
        <Field label="Language (BCP-47)"><Input value={draft.seo?.language || "en-IN"} onChange={(e) => set("seo.language", e.target.value)} placeholder="en-IN" /></Field>
        <Field label="Theme colour (mobile browser chrome)">
          <div className="flex items-center gap-2">
            <input type="color" value={draft.seo?.theme_color || "#F26722"} onChange={(e) => set("seo.theme_color", e.target.value)} className="h-9 w-14 cursor-pointer rounded border border-border bg-transparent" />
            <Input value={draft.seo?.theme_color || ""} onChange={(e) => set("seo.theme_color", e.target.value)} className="font-mono text-xs" />
          </div>
        </Field>
        <FieldFull label="Favicon">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              {(draft.brand?.favicon || draft.seo?.favicon_url) ? (
                <img
                  src={draft.brand?.favicon || draft.seo?.favicon_url}
                  alt="favicon preview"
                  className="h-10 w-10 rounded border border-border/60 bg-white p-1"
                  data-testid="favicon-preview"
                />
              ) : (
                <div className="grid h-10 w-10 place-items-center rounded border border-dashed border-border text-[10px] uppercase text-muted-foreground">32×32</div>
              )}
              <label className="cursor-pointer rounded-md border border-border/60 bg-card px-3 py-2 text-xs font-semibold hover:bg-muted">
                <input
                  type="file"
                  accept="image/png,image/x-icon,image/svg+xml,image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 100 * 1024) { alert("Favicon must be under 100 KB"); return; }
                    const reader = new FileReader();
                    reader.onload = () => set("brand.favicon", reader.result);
                    reader.readAsDataURL(f);
                  }}
                  data-testid="favicon-upload"
                />
                Upload favicon
              </label>
              {draft.brand?.favicon && (
                <button
                  type="button"
                  onClick={() => set("brand.favicon", "")}
                  className="text-xs text-destructive hover:underline"
                  data-testid="favicon-clear"
                >Remove uploaded</button>
              )}
            </div>
            <Input
              value={draft.seo?.favicon_url || ""}
              onChange={(e) => set("seo.favicon_url", e.target.value)}
              placeholder="…or paste a URL: https://…/favicon.ico"
              data-testid="favicon-url"
            />
            <div className="text-[10px] text-muted-foreground">Uploaded file (max 100 KB) takes priority over the URL. Recommended: 32×32 or 64×64 PNG / ICO / SVG.</div>
          </div>
        </FieldFull>
        <Field label="Geo region (ISO country/state)"><Input value={draft.seo?.geo_region || ""} onChange={(e) => set("seo.geo_region", e.target.value)} placeholder="IN" /></Field>
        <Field label="Geo place name"><Input value={draft.seo?.geo_placename || ""} onChange={(e) => set("seo.geo_placename", e.target.value)} placeholder="Pune, Maharashtra, India" /></Field>
        <FieldFull label="Google Tag Manager container ID">
          <Input
            value={draft.analytics?.gtm_id || ""}
            onChange={(e) => set("analytics.gtm_id", e.target.value.trim())}
            placeholder="GTM-XXXXXXX"
            className="font-mono"
            data-testid="gtm-id-input"
          />
          <div className="mt-1 text-[10px] text-muted-foreground">
            Paste your GTM container ID (format <Code>GTM-XXXXXXX</Code>). When set, the GTM head-script + noscript-iframe are injected on every page.
            Leave empty to disable. Find your ID at <a href="https://tagmanager.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">tagmanager.google.com</a>.
          </div>
        </FieldFull>
        <FieldFull label="Google Analytics 4 measurement ID (direct gtag.js)">
          <Input
            value={draft.analytics?.ga4_measurement_id || ""}
            onChange={(e) => set("analytics.ga4_measurement_id", e.target.value.trim())}
            placeholder="G-XXXXXXXXXX"
            className="font-mono"
            data-testid="ga4-id-input"
          />
          <div className="mt-1 text-[10px] text-muted-foreground">
            Paste your GA4 measurement ID (format <Code>G-XXXXXXXXXX</Code>) for direct <Code>gtag.js</Code> injection —
            use this when you're <b>not</b> using GTM but still want GA4 tracking. Both can co-exist safely.
            Leave empty to disable. Find your ID in <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">GA4 → Admin → Data Streams</a>.
          </div>
        </FieldFull>
        <FieldFull label="Structured data (JSON-LD schema.org)">
          <div className="flex items-center gap-3">
            <input type="checkbox" id="cms-jsonld-toggle" checked={draft.seo?.structured_data_enabled !== false} onChange={(e) => set("seo.structured_data_enabled", e.target.checked)} className="h-4 w-4 rounded border-input" data-testid="cms-seo-jsonld" />
            <label htmlFor="cms-jsonld-toggle" className="text-sm">Emit Organization, LocalBusiness, WebSite &amp; FAQ schemas for rich results</label>
          </div>
        </FieldFull>
      </Grid2>

      <div className="rounded-md border border-border/60 bg-muted/30 p-4">
        <div className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">SERP Preview</div>
        <div>
          <div className="truncate text-lg text-[#1a0dab]" style={{ fontFamily: "arial,sans-serif" }}>{draft.seo?.title || "Untitled page"}</div>
          <div className="text-xs text-[#006621]" style={{ fontFamily: "arial,sans-serif" }}>{draft.seo?.canonical_url || "https://your-site.com/"}</div>
          <div className="mt-1 text-sm text-[#545454]" style={{ fontFamily: "arial,sans-serif" }}>{(draft.seo?.description || "").slice(0, 160) || "No meta description set."}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <a href="/robots.txt" target="_blank" rel="noreferrer" className="rounded border border-border px-3 py-1.5 hover:bg-muted">View /robots.txt →</a>
        <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="rounded border border-border px-3 py-1.5 hover:bg-muted">View /sitemap.xml →</a>
      </div>
    </>
  );
}
